import { createContext, ReactNode, useContext, useEffect, useMemo, useState } from 'react';
import { useOnlineStatus } from '../hooks/useOnlineStatus';
import { useWindowSize } from '../hooks/useWindowSize';

interface SystemContextValue {
  online: boolean;
  windowSize: { width: number; height: number };
  platform: 'web' | 'ios' | 'android' | 'desktop' | 'unknown';
}

const SystemContext = createContext<SystemContextValue | undefined>(undefined);

function detectPlatform(): SystemContextValue['platform'] {
  if (typeof navigator === 'undefined') return 'unknown';
  const ua = navigator.userAgent || navigator.vendor || (window as any).opera || '';
  if (/android/i.test(ua)) return 'android';
  if (/iPad|iPhone|iPod/.test(ua) && !(window as any).MSStream) return 'ios';
  if (/Mac|Win|Linux/i.test(ua)) return 'desktop';
  return 'web';
}

export function SystemProvider({ children }: { children: ReactNode }) {
  const online = useOnlineStatus();
  const windowSize = useWindowSize();
  const [platform, setPlatform] = useState<SystemContextValue['platform']>('unknown');

  useEffect(() => {
    setPlatform(detectPlatform());
  }, []);

  const value = useMemo(() => ({ online, windowSize, platform }), [online, windowSize, platform]);

  return <SystemContext.Provider value={value}>{children}</SystemContext.Provider>;
}

export function useSystem() {
  const ctx = useContext(SystemContext);
  if (!ctx) throw new Error('useSystem must be used within SystemProvider');
  return ctx;
}
